import AutocompleteSearchInput from '@/components/form/AutocompleteSearchInput'
import { FloristStatusBadge } from '@/components/organizations/FloristStatusBadge'
import { ActionRow } from '@/components/patterns/action-row'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import {
  FloristMap,
  hasPin,
  showWholeCountry,
  type PinnedFloristLocation,
} from '@/components/reports/FloristMap'
import { Button } from '@/components/ui/button'
import { AddressFormatter } from '@/components/ui/address-formatter'
import { Card } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Typography } from '@/components/ui/typography'
import { formatPhone } from '@/hooks/use-phone-formatter'
import { usePlacesAutocomplete, type PlaceSuggestion } from '@/hooks/use-places-autocomplete'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminTitleHead } from '@/lib/admin-route-title'
import { isGlobalOrganization } from '@/lib/user-access'
import {
  floristStatusSchema,
  type FloristStatusFilter,
} from '@/lib/services/organizations/organization.schema'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import { reportQueries } from '@/lib/services/reports/reports.query'
import {
  DEFAULT_FLORIST_MAP_STATUSES,
  floristMapSearchSchema,
} from '@/lib/services/reports/reports.schema'
import type { FloristMapLocation } from '@/lib/services/reports/reports.types'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router'
import { ChevronDown, MapPinned } from 'lucide-react'
import { useDebounce } from '@uidotdev/usehooks'
import { useEffect, useMemo, useRef, useState } from 'react'

export const Route = createFileRoute('/_authenticated/$orgId/reports/florist-map/')({
  component: FloristMapReportPage,
  head: () => adminTitleHead('Florist Map', 'Reports'),
  validateSearch: (search) => floristMapSearchSchema.parse(search),
  loader: async ({ context, params }) => {
    if (!context.ability.can('access', 'global')) throw notFound()

    const organization =
      context.queryClient.getQueryData?.(organizationQueries.show(params.orgId).queryKey) ??
      (await context.queryClient.ensureQueryData(organizationQueries.show(params.orgId)))

    if (!isGlobalOrganization(organization)) throw notFound()

    void context.queryClient.prefetchQuery(
      reportQueries.floristMap({ organizationId: params.orgId })
    )

    return { isReadOnly: true }
  },
})

function byShopName(a: FloristMapLocation, b: FloristMapLocation): number {
  return a.organization_name.localeCompare(b.organization_name)
}

export function FloristMapReportPage() {
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { data: locations } = useSuspenseQuery(reportQueries.floristMap({ organizationId: orgId }))
  const { fetchSuggestions, getPlaceViewport } = usePlacesAutocomplete()
  const mapRef = useRef<google.maps.Map | null>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const placeInputRef = useRef<HTMLInputElement>(null)
  const [bounds, setBounds] = useState<google.maps.LatLngBounds | null>(null)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [place, setPlace] = useState('')
  // Google's suggestions, with the text they answer.
  const [placeSuggestions, setPlaceSuggestions] = useState<{
    query: string
    suggestions: PlaceSuggestion[]
  }>({ query: '', suggestions: [] })
  const debouncedPlace = useDebounce(place, 300)

  const statuses: readonly string[] = search.status ?? DEFAULT_FLORIST_MAP_STATUSES
  const shown = locations.filter((location) => statuses.includes(location.florist_status))
  // Memoized so the map rebuilds its pins only when the data or status filter changes, not on
  // every pan. The router keeps `search.status` the same array until the URL changes.
  const pinned = useMemo(
    () => locations.filter(hasPin).filter((location) => statuses.includes(location.florist_status)),
    [locations, statuses]
  )
  const inView = pinned
    .filter((location) => !bounds || bounds.contains({ lat: location.lat, lng: location.lng }))
    .toSorted(byShopName)
  const unpinned = shown.filter((location) => !hasPin(location)).toSorted(byShopName)
  // A shop the status filter hides is no longer selected.
  const selected = shown.find((location) => location.id === selectedId) ?? null
  const floristCount = new Set(shown.map((location) => location.organization_id)).size
  const stateCount = new Set(shown.flatMap((location) => location.state ?? [])).size

  // Google's place suggestions are an outside system; ask again once the typed place settles.
  useEffect(() => {
    if (debouncedPlace.trim().length < 3) return

    let isCurrent = true
    void fetchSuggestions(debouncedPlace, { includedPrimaryTypes: ['(regions)'] }).then(
      (suggestions) => {
        if (isCurrent) setPlaceSuggestions({ query: debouncedPlace, suggestions })
      }
    )

    return () => {
      isCurrent = false
    }
  }, [debouncedPlace, fetchSuggestions])

  const setStatus = (status: FloristStatusFilter, isShown: boolean) => {
    void navigate({
      search: (previous) => ({
        ...previous,
        status: floristStatusSchema.options.filter((option) =>
          option === status ? isShown : statuses.includes(option)
        ),
      }),
      replace: true,
    })
  }

  const showPlace = async (suggestion: PlaceSuggestion) => {
    setPlace(suggestion.name)
    // Searching is done; leaving the box keeps the suggestions for the new text from reopening.
    placeInputRef.current?.blur()
    const viewport = await getPlaceViewport(suggestion.value)
    if (viewport) mapRef.current?.fitBounds(viewport, 0)
  }

  // Back to the opening view: the whole country, nothing picked, no place searched.
  const resetZoom = () => {
    setSelectedId(null)
    setPlace('')
    if (mapRef.current) showWholeCountry(mapRef.current)
  }

  const selectFromMap = (location: PinnedFloristLocation) => {
    setSelectedId(location.id)
    const list = listRef.current
    const row = list?.querySelector(`[data-location-id="${location.id}"]`)
    if (!list || !row) return

    // Scroll only the list, centering the row: scrollIntoView would also scroll the page, which
    // moves the map out of sight when the list sits below it on narrow screens.
    const rowTop = row.getBoundingClientRect().top - list.getBoundingClientRect().top
    list.scrollTo({ top: list.scrollTop + rowTop - (list.clientHeight - row.clientHeight) / 2 })
  }

  const selectFromList = (location: FloristMapLocation) => {
    setSelectedId(location.id)
    if (!hasPin(location)) return

    // Bring the map to the shop; the pop-up opens over it.
    mapRef.current?.panTo({ lat: location.lat, lng: location.lng })
    mapRef.current?.setZoom(Math.max(mapRef.current.getZoom() ?? 0, 14))
  }

  return (
    <div className="@container/florist-map w-full space-y-4 pb-6">
      <AdminPageHeader
        icon={MapPinned}
        title="Florist Map"
        description="Where OneStem florists are. Search a ZIP code, city or state to jump there."
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full max-w-sm">
          <AutocompleteSearchInput
            inputRef={placeInputRef}
            aria-label="Jump to a ZIP code, city or state"
            placeholder="Jump to a ZIP code, city or state"
            value={place}
            setValue={setPlace}
            // Only suggestions for exactly what's typed: an answer for earlier text (or one still
            // arriving after more typing) stays hidden, so Enter can't pick an old place.
            suggestions={placeSuggestions.query === place ? placeSuggestions.suggestions : []}
            onSelect={(suggestion) => void showPlace(suggestion)}
            getOptionKey={(suggestion) => suggestion.id}
            getOptionLabel={(suggestion) => suggestion.name}
          />
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm">
              Status ({statuses.length})
              <ChevronDown className="size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {floristStatusSchema.options.map((status) => (
              <DropdownMenuCheckboxItem
                key={status}
                checked={statuses.includes(status)}
                onCheckedChange={(checked) => setStatus(status, checked)}
                onSelect={(event) => event.preventDefault()}
              >
                <FloristStatusBadge status={status} />
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Typography variant="supporting" tone="muted">
          {floristCount} florists · {shown.length} locations · {stateCount} states
        </Typography>
        <Button type="button" variant="link" size="inline" className="ml-auto" onClick={resetZoom}>
          Reset zoom
        </Button>
      </div>

      <div className="grid gap-4 @4xl/florist-map:grid-cols-[minmax(0,1fr)_22rem]">
        <Card size="flush" className="overflow-hidden">
          <FloristMap
            locations={pinned}
            mapRef={mapRef}
            selectedId={selectedId}
            popup={
              selected && hasPin(selected) ? (
                <FloristLocationDetails orgId={orgId} location={selected} />
              ) : null
            }
            onBoundsChange={setBounds}
            onSelect={selectFromMap}
            onDeselect={() => setSelectedId(null)}
            className="h-[70vh] min-h-96"
          />
        </Card>

        <Card ref={listRef} size="flush" className="h-[70vh] min-h-96 overflow-y-auto p-2">
          <Typography variant="eyebrow" tone="muted" className="px-2 py-1">
            {inView.length} in view
          </Typography>
          {inView.map((location) => (
            <ShopRow
              key={location.id}
              location={location}
              selected={location.id === selectedId}
              onSelect={selectFromList}
            />
          ))}
          {unpinned.length > 0 ? (
            <>
              <Typography variant="eyebrow" tone="muted" className="mt-3 px-2 py-1">
                No map pin ({unpinned.length})
              </Typography>
              {unpinned.map((location) => (
                <div key={location.id}>
                  <ShopRow
                    location={location}
                    selected={location.id === selectedId}
                    onSelect={selectFromList}
                  />
                  {/* Without a pin there's nowhere on the map for the pop-up, so it opens here. */}
                  {location.id === selectedId ? (
                    <FloristLocationDetails orgId={orgId} location={location} />
                  ) : null}
                </div>
              ))}
            </>
          ) : null}
        </Card>
      </div>
    </div>
  )
}

function ShopRow({
  location,
  selected,
  onSelect,
}: {
  location: FloristMapLocation
  selected: boolean
  onSelect: (location: FloristMapLocation) => void
}) {
  return (
    <ActionRow
      padding="compact"
      selected={selected}
      data-location-id={location.id}
      onClick={() => onSelect(location)}
    >
      <span className="min-w-0">
        <Typography as="span" variant="label" className="block truncate">
          {location.organization_name}
        </Typography>
        <Typography as="span" variant="supporting" tone="muted" className="block">
          <AddressFormatter variant="cityState" address={location} />
        </Typography>
      </span>
    </ActionRow>
  )
}

/** A shop's details: in the map's pop-up, or in the list when the shop has no map pin. */
function FloristLocationDetails({
  orgId,
  location,
}: {
  orgId: number
  location: FloristMapLocation
}) {
  const path = usePathHelper()

  return (
    <div className="w-72 max-w-full space-y-2 p-3">
      <div className="flex items-start justify-between gap-2">
        <Typography variant="labelStrong">{location.organization_name}</Typography>
        <FloristStatusBadge status={location.florist_status} />
      </div>
      <Typography as="div" variant="supporting">
        <AddressFormatter variant="noCountry" address={location} />
      </Typography>
      {location.phone ? (
        <Typography as="p" variant="supporting">
          {formatPhone(location.phone)}
        </Typography>
      ) : null}
      {location.primary_domain ? (
        <Button asChild variant="link" size="inline" className="max-w-full">
          <a href={`https://${location.primary_domain}`} target="_blank" rel="noreferrer">
            {/* A long domain wraps instead of widening the pop-up into a scrollbar. */}
            <span className="min-w-0 break-all whitespace-normal">{location.primary_domain}</span>
          </a>
        </Button>
      ) : null}
      <div className="flex flex-wrap gap-2 pt-1">
        <Button asChild variant="outline" size="sm">
          <Link {...path.organizations.show(location.organization_id).using({ orgId }).link}>
            View florist
          </Link>
        </Button>
        {hasPin(location) ? null : (
          <Button asChild variant="outline" size="sm">
            <Link
              {...path.locations.show(location.id).using({ orgId: location.organization_id }).link}
            >
              Add a map pin
            </Link>
          </Button>
        )}
      </div>
    </div>
  )
}
